import { DateTime } from 'luxon'

import SeriesQ from '@/queries/SeriesQ'
import SeriesTemplateQ from '@/queries/SeriesTemplateQ'
import SessionQ from '@/queries/SessionQ'
import UserQ from '@/queries/UserQ'
import type { Query } from '@/client/query'
import { SPRING_2025_ALGEBRA2_FINAL_EXAM_PREP_START_DATES } from '@/client/services/programs/ka-learning-labs/algebra2/constants'
import { getTutorOfferingPageUrl } from '@/client/services/url'
import { isProgramWorld } from '@/client/services/worlds/helpers'

import { PROGRAM_SLUG_TO_PROGRAM_DETAILS } from './config'
import {
  POPULAR_RECURRING_PROGRAM_TIME_RANGES,
  PROGRAM_SLUGS,
  PROGRAM_WORLD_TO_PROGRAM_SLUG,
  type ProgramSlug,
} from './constants'
import type {
  ProgramOfferingInstance,
  ProgramOfferingTemplateWithDates,
  ProgramSeriesDetails,
  RegistrationStatus,
} from './types'

export function programSeriesDetailsFromQuery(series: Query): ProgramSeriesDetails {
  const sessionStartDates = series
    .many(SessionQ.where({ seriesId: SeriesQ.id, order: [[SessionQ.start_date, 'asc']] }))
    .map(sessionItem => `${sessionItem(SessionQ.start_date)}`)
  const seriesTemplate = series.one(SeriesQ.getSeriesTemplate())
  const tutor = series.one(UserQ.where({ id: SeriesQ.user_id }))

  return {
    id: series(SeriesQ.id) ?? 0,
    templateSlug: seriesTemplate(SeriesTemplateQ.slug) ?? '',
    sessionStartDates,
    tutorName: tutor(UserQ.display_name) ?? '',
    tutorAvatarUrl: tutor(UserQ.avatar_url) ?? '',
    tutorBio: tutor(UserQ.bio) ?? '',
    countRegistrationsAndReservations:
      series(SeriesQ.countRegistrationsAndActiveReservations()) ?? 0,
    maxLearners: series(SeriesQ.max_learners) ?? 0,
    attendancePolicy: series(SeriesQ.attendance_policy) ?? '',
    questionnaire: series(SeriesQ.questionnaire) ?? null,
    researchGroup: series(SeriesQ.getResearchGroup()) ?? null,
  }
}

export function getProgramPopularTimeRange(type: 'weekday' | 'weekend'): string {
  const startDateTime = DateTime.fromObject(
    {
      hour: POPULAR_RECURRING_PROGRAM_TIME_RANGES[type].startHour,
    },
    {
      zone: POPULAR_RECURRING_PROGRAM_TIME_RANGES.timezone,
    }
  ).setZone('local')
  const endDateTime = DateTime.fromObject(
    {
      hour: POPULAR_RECURRING_PROGRAM_TIME_RANGES[type].endHour,
    },
    {
      zone: POPULAR_RECURRING_PROGRAM_TIME_RANGES.timezone,
    }
  ).setZone('local')

  const startDateTimeString = startDateTime.toLocaleString(DateTime.TIME_SIMPLE)
  const endDateTimeString = endDateTime.toLocaleString(DateTime.TIME_SIMPLE)

  return `${startDateTimeString} - ${endDateTimeString} ${startDateTime.offsetNameShort}`
}

export function isDuringAlgebra2FinalExamPrepExperiment(): boolean {
  return (
    DateTime.now() < SPRING_2025_ALGEBRA2_FINAL_EXAM_PREP_START_DATES['cohort-6'].plus({ weeks: 4 })
  )
}

export function isProgramSlug(slug: string): slug is ProgramSlug {
  return PROGRAM_SLUGS.some(programSlug => programSlug === slug)
}

const PROGRAMS_REQUIRING_CONSENT = new Set<string>([])
const PROGRAMS_REQUIRING_ASSENT = new Set<string>([])

export function needsConsentForProgram(programSlug: string): {
  needsParentConsent: boolean
  needsAssent: boolean
} {
  return {
    needsParentConsent: PROGRAMS_REQUIRING_CONSENT.has(programSlug) ?? false,
    needsAssent: PROGRAMS_REQUIRING_ASSENT.has(programSlug) ?? false,
  }
}

// Iteration programs use a different series template slug per iteration (e.g. 'caw-essay-2026-03'),
// so we override the base template's optional templateSlug with the iteration-specific one.
// This is a temporary pattern until we move to versioned global templates (SCH-47).
export function resolveIterationOfferingTemplate(
  offering: ProgramOfferingInstance,
  registrationStatus: RegistrationStatus | null
): ProgramOfferingTemplateWithDates {
  return {
    ...offering.offeringTemplate,
    templateSlug: offering.seriesTemplateSlug,
    dates: [{ startDate: offering.startDate, endDate: offering.endDate }],
    registrationStatus,
  }
}

export function getTutorOfferingPageUrlByWorld(worldSlug: string, subpage?: 'certify'): string {
  if (isProgramWorld(worldSlug)) {
    return getTutorOfferingPageUrl({
      type: 'program',
      slug: PROGRAM_WORLD_TO_PROGRAM_SLUG[worldSlug],
      subpage,
    })
  }
  return getTutorOfferingPageUrl({ type: 'subject', slug: worldSlug, subpage })
}

export function getMonthlyProgramSeriesTemplateSwitchLimit(templateSlug: string): number | null {
  for (const programDetails of Object.values(PROGRAM_SLUG_TO_PROGRAM_DETAILS)) {
    if (programDetails.monthlySwitchLimit != null) {
      const matchingTemplate = programDetails.offeringTemplates?.find(
        t => t.templateSlug === templateSlug
      )
      if (matchingTemplate != null) {
        return programDetails.monthlySwitchLimit
      }
    }
  }
  return null
}
